import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

// ── ステップ定義 ──────────────────────────────────────────────────
const STEPS = [
    {
        label: '業者探し',
        desc: 'エリアや工事内容から業者を検索し、チャットで問い合わせます。',
    },
    {
        label: '相談・見積',
        desc: '業者とチャットで工事内容を相談し、見積書を受け取ります。複数社に声をかけて比較できます。',
    },
    {
        label: '業者決定',
        desc: '見積書を比較し、1社を選んで発注します。未選定の業者には自動で通知が送られます。',
    },
    {
        label: '請求・支払',
        desc: '業者から請求書が届きます。内容を確認してお支払いください。工事完了が確認されるまで、業者への代金のお支払いは行われません。',
    },
    {
        label: '工事日程',
        desc: '業者が工事の日程を登録します。日程が確定したらお知らせします。',
    },
    {
        label: '工事中',
        desc: '現場での工事が進行します。疑問点はチャットで業者に直接相談できます。',
    },
    {
        label: '完了確認',
        desc: '工事完了後、仕上がりを確認してください。確認後に業者への支払いが実行されます。',
    },
    {
        label: '完了',
        desc: 'すべての手続きが完了しました。ありがとうございました。',
    },
];

const STATUS_TO_STEP = {
    chatting: 1, estimated: 1,
    decided: 2,
    invoiced: 3, paid: 3,
    scheduled: 4,
    in_progress: 5,
    completion_notified: 6,
    completed: 7,
};

const STATUS_BADGE = {
    chatting:            { label: 'すり合わせ中',        bg: '#e3f2fd', color: '#1565c0' },
    estimated:           { label: '見積が届いています',   bg: '#fff3e0', color: '#e65100' },
    decided:             { label: '発注済み',             bg: '#e8f5e9', color: '#2e7d32' },
    invoiced:            { label: '請求書が届いています', bg: '#fce4ec', color: '#c62828' },
    paid:                { label: '支払い済み',           bg: '#e8f5e9', color: '#2e7d32' },
    scheduled:           { label: '工事日程確定',         bg: '#e8eaf6', color: '#283593' },
    in_progress:         { label: '工事中',               bg: '#e0f2f1', color: '#00695c' },
    completion_notified: { label: '完了確認待ち',         bg: '#fff8e1', color: '#f57f17' },
};

const ACTION = {
    estimated:           { label: '見積書を確認する',  path: (id) => `/customer/estimate/${id}`,   urgent: true },
    invoiced:            { label: '請求書を確認する',  path: (id) => `/customer/invoice/${id}`,    urgent: true },
    completion_notified: { label: '完了確認をする',    path: (id) => `/customer/completion/${id}`, urgent: false },
};

const WAIT_MSG = {
    chatting:    '業者とすり合わせ中です',
    decided:     '請求書の到着を待っています',
    paid:        '工事日程の連絡を待っています',
    scheduled:   '工事開始を待っています',
    in_progress: '工事が進行中です',
};

const MATCHING = new Set(['chatting', 'estimated']);

// ── アイコン ──────────────────────────────────────────────────────
const CheckIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const ArrowIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);

// ── ステップカード（現在地の展開表示） ───────────────────────────
const CurrentStepDetail = ({ currentStepIndex, matchingProjects, decidedProject, navigate }) => {
    // マッチング段階
    if (currentStepIndex === 1) {
        const withEstimate = matchingProjects.filter(p => p.status === 'estimated').length;
        const total = matchingProjects.length;
        const badge = total > 0 ? STATUS_BADGE[withEstimate > 0 ? 'estimated' : 'chatting'] : null;

        return (
            <div style={{ marginTop: 10, padding: '14px 16px', background: '#f5f6f8', borderRadius: 8 }}>
                {total > 0 ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.88rem', color: '#1a1a2e', fontWeight: 500 }}>
                                {total}社と相談中
                                {withEstimate > 0 && `（見積あり ${withEstimate}社）`}
                            </span>
                            {badge && (
                                <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: badge.bg, color: badge.color }}>
                                    {badge.label}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => navigate('/customer/dashboard')}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 16px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                            ホームで確認する <ArrowIcon />
                        </button>
                    </>
                ) : (
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>相談中の業者がいません</p>
                )}
            </div>
        );
    }

    // 業者決定後
    if (!decidedProject) return null;

    const p       = decidedProject;
    const badge   = STATUS_BADGE[p.status];
    const action  = ACTION[p.status];
    const waitMsg = WAIT_MSG[p.status];
    const name    = p.contractor?.company_name || `業者 #${p.contractor_id}`;

    return (
        <div style={{ marginTop: 10, padding: '14px 16px', background: '#f5f6f8', borderRadius: 8 }}>
            <p style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, letterSpacing: '0.05em', margin: '0 0 4px' }}>担当業者</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontSize: '0.97rem', fontWeight: 700, color: '#1a1a2e' }}>{name}</span>
                {badge && (
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 500, background: badge.bg, color: badge.color }}>
                        {badge.label}
                    </span>
                )}
            </div>
            {waitMsg && !action && (
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 10px' }}>{waitMsg}</p>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                    onClick={() => navigate(`/customer/chat/${p.id}`)}
                    style={{ padding: '8px 14px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.84rem', cursor: 'pointer' }}
                >
                    チャット
                </button>
                {action && (
                    <button
                        onClick={() => navigate(action.path(p.id))}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 18px', background: action.urgent ? '#e65100' : '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                        {action.label} <ArrowIcon />
                    </button>
                )}
            </div>
        </div>
    );
};

// ── メイン ────────────────────────────────────────────────────────
const CustomerProgress = () => {
    const customerId = localStorage.getItem('customer_id');
    const navigate   = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading]   = useState(true);

    useEffect(() => {
        api.get(`projects/by_customer/${customerId}`)
            .then(r => setProjects(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [customerId]);

    const activeProjects   = projects.filter(p => !['lost', 'completed'].includes(p.status));
    const matchingProjects = activeProjects.filter(p => MATCHING.has(p.status));
    const decidedProject   = activeProjects.find(p => !MATCHING.has(p.status));

    const currentStepIndex = activeProjects.length === 0 ? -1
        : Math.max(...activeProjects.map(p => STATUS_TO_STEP[p.status] ?? 0));

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8' }}>

            {/* ページヘッダー */}
            <div style={{ background: '#141c3d', padding: '32px 0' }}>
                <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 16px' }}>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>進行状況</h1>
                    <p style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.55)', margin: 0 }}>
                        依頼の流れと現在地を確認できます
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 16px' }}>

                {/* 案件なし */}
                {activeProjects.length === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 0' }}>
                        <p style={{ fontSize: '0.95rem', color: '#6b7280', margin: '0 0 20px' }}>現在進行中の案件はありません</p>
                        <button
                            onClick={() => navigate('/customer/search')}
                            style={{ padding: '10px 24px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
                        >
                            業者を探す
                        </button>
                    </div>
                )}

                {/* 縦タイムライン */}
                {activeProjects.length > 0 && (
                    <div style={{ background: '#fff', borderRadius: 10, padding: '24px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                        {STEPS.map((step, i) => {
                            const isPast   = i < currentStepIndex;
                            const isActive = i === currentStepIndex;
                            const isLast   = i === STEPS.length - 1;

                            return (
                                <div key={i} style={{ display: 'flex', gap: 16 }}>

                                    {/* 左：サークル＋縦線 */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                        <div style={{
                                            width: isActive ? 30 : 24,
                                            height: isActive ? 30 : 24,
                                            borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                            background: isPast ? '#005c53' : isActive ? '#f59e0b' : '#fff',
                                            border: `2px solid ${isPast ? '#005c53' : isActive ? '#f59e0b' : '#d1d5db'}`,
                                            color: isPast || isActive ? '#fff' : '#9ca3af',
                                            fontSize: '0.72rem', fontWeight: 700,
                                            boxShadow: isActive ? '0 0 0 4px rgba(245,158,11,0.18)' : 'none',
                                            transition: 'all 0.2s',
                                        }}>
                                            {isPast ? <CheckIcon /> : i + 1}
                                        </div>
                                        {!isLast && (
                                            <div style={{
                                                width: 2, flex: 1, minHeight: 20, margin: '4px 0',
                                                background: isPast ? '#005c53' : '#e5e7eb',
                                            }} />
                                        )}
                                    </div>

                                    {/* 右：コンテンツ */}
                                    <div style={{ flex: 1, paddingBottom: isLast ? 0 : 20 }}>
                                        <div style={{
                                            fontSize: isActive ? '1rem' : '0.9rem',
                                            fontWeight: isActive ? 700 : isPast ? 500 : 400,
                                            color: isActive ? '#1a1a2e' : isPast ? '#6b7280' : '#9ca3af',
                                            paddingTop: isActive ? 3 : 2,
                                            lineHeight: 1.4,
                                        }}>
                                            {step.label}
                                            {isActive && (
                                                <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 600, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 10 }}>
                                                    現在地
                                                </span>
                                            )}
                                        </div>

                                        {/* 現在地のみ：説明文＋詳細 */}
                                        {isActive && (
                                            <>
                                                <p style={{ fontSize: '0.83rem', color: '#6b7280', margin: '6px 0 0', lineHeight: 1.7 }}>
                                                    {step.desc}
                                                </p>
                                                <CurrentStepDetail
                                                    currentStepIndex={currentStepIndex}
                                                    matchingProjects={matchingProjects}
                                                    decidedProject={decidedProject}
                                                    navigate={navigate}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default CustomerProgress;
